/**
 * REQUEST MISSING INFO DIALOG — Modale di composizione email di reply
 * per richiedere le MessageInfo mancanti.
 *
 * Stile allineato a TaskForm (classi .app-dialog/__header/__body/__footer).
 *
 * Il body e' strutturato come reply email classica:
 *   - testo precompilato con elenco MessageInfo vuote (prefisso "- ")
 *   - blocco "quoted" del messaggio originale (header "Il ... ha scritto:"
 *     + ogni riga del body prefissata con "> "), come fa un client email
 *     standard.
 *
 * Invio: stub console.log + toast. Quando il backend esporra' un
 * endpoint /messages/{id}/reply o simili, sostituire `handleSend` con
 * la chiamata reale e rimuovere il log.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import type { Message } from './messageTypes';
import { splitInfos, getLocalizedLabel } from './messageInfoTypes';

export function RequestMissingInfoDialog({
  message,
  onClose,
}: {
  message: Message;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const userEmail = useAppSelector((state) => state.auth.user?.emailAddress ?? '');

  // MessageInfo con value vuoto = informazioni mancanti (stessa logica
  // della sezione "Informazioni mancanti" nel detail).
  const missingLabels = useMemo(
    () =>
      splitInfos((message.extractorInfos ?? []).flatMap((g) => g.infos)).missing.map(
        (i) => getLocalizedLabel(i, t),
      ),
    [message.extractorInfos, t],
  );

  // Subject default: "Re: <original>" (no duplicate se il subject e'
  // gia' una reply).
  const defaultSubject = useMemo(() => {
    const trimmed = message.subject.trim();
    return /^re:\s*/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
  }, [message.subject]);

  // Body default: testo precompilato con elenco MessageInfo mancanti +
  // blocco quoted del messaggio originale (prefisso "> " su ogni riga).
  const defaultBody = useMemo(() => {
    const list = missingLabels.length > 0
      ? missingLabels.map((l) => `- ${l}`).join('\n')
      : t('message.requestMissing.noMissing');
    const receivedDate = (() => {
      try {
        return new Date(message.receiptDate).toLocaleString();
      } catch {
        return message.receiptDate;
      }
    })();
    const quotedHeader = t('message.requestMissing.quotedHeader', {
      date: receivedDate,
      name: message.senderName,
      email: message.senderEmailAddress,
    });
    const quotedBody = message.body
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');
    const quoted = `${quotedHeader}\n${quotedBody}`;
    return t('message.requestMissing.bodyTemplate', { list, quoted });
  }, [missingLabels, message.body, message.receiptDate, message.senderEmailAddress, message.senderName, t]);

  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  function handleSend() {
    // TODO(message-request-missing): sostituire con call al backend di
    // invio mail quando disponibile (es. POST /messages/{id}/reply).
    // Finche' e' stub il toast e' `info` (non `success`): non vogliamo
    // far credere all'utente che la mail sia partita davvero.
    console.log('[RequestMissingInfo] send email (stub)', {
      from: userEmail,
      to: message.senderEmailAddress,
      cc,
      subject,
      body,
    });
    toast({ severity: 'info', message: t('message.requestMissing.toastStub') });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('message.requestMissing.title')}</span>
        <IconButton
          aria-label={t('message.requestMissing.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <TextField
          label={t('message.requestMissing.from')}
          value={userEmail}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
          className="form-field"
        />
        <TextField
          label={t('message.requestMissing.to')}
          value={message.senderEmailAddress}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
          className="form-field"
        />
        <TextField
          label={t('message.requestMissing.cc')}
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          placeholder={t('message.requestMissing.ccPlaceholder')}
          fullWidth
          size="small"
          className="form-field"
        />
        <TextField
          label={t('message.requestMissing.subject')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          size="small"
          className="form-field"
        />
        <TextField
          label={t('message.requestMissing.body')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          rows={14}
          className="form-field"
        />
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button onClick={handleSend} variant="contained">
          {t('message.requestMissing.send')}
        </Button>
        <Button onClick={onClose} variant="outlined">
          {t('message.requestMissing.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
